/**
 * This composable is an utility to check dependencies between fields in a form.
 * e.g. field A should be shown only if field B has certain value selected
 */
export default function () {
  /**
   * Use DOM traversal to check whether the dependency on another field is fulfilled or not.
   * - This means that both fields have to be under the same parent.
   * - Each field also needs to have attribute "data-wdw-code='<code>'" in its input field
   * You can use this method for checking between 2 different inputs. For whole Form-checking, use evaluateFormIfCode.
   *
   * TODO possibly use jQuery instead of normal JS for DOM manipulation???
   *
   * @param ifCode
   * @param ifValue
   * @param parentFormId
   */
  function evaluateIfCodeValue(ifCode: string, ifValue: string, parentFormId: string) {
    if (ifCode == null || ifCode === '' || ifValue == null || parentFormId == null || parentFormId === '') return true;
    const parentForm = document.getElementById(parentFormId);
    const field = parentForm?.querySelector("[data-wdw-code='" + ifCode + "']") as HTMLInputElement;
    if (field) {
      let realValue: any = field.value;
      let comparedValue: any = ifValue;
      if (field.type === 'checkbox') {
        realValue = field.checked;
        comparedValue = ifValue === '1' || ifValue === 'true';
      } else if (field.type === 'select') {
        realValue = field.value[0];
      }
      return comparedValue === realValue;
    }
    return true;
  }

  /**
   * Evaluate the whole form from a change on the given field. It is recommended that all fields checked should be within a parent form
   *
   * @param parentFormId ID of the parent form
   * @param ifKey the key on each element in fieldsList that points to the ifCode (e.g. xxx_if_code)
   * @param ifValue the key on each element in fieldsList that points to the ifValue (e.g. xxx_if_value)
   * @param previousEvaluation the list of last evaluations. If the ifCode doesn't apply, it will take the previous evaluation result (or true as fallback)
   * @param fieldsList List of fields.
   *      Example of each element:
   *      { code: "<code>", type: "<field type>", "<ifKey>": "<if-code>", "<valueKey>": "<value-for-if-to-be-true>" }
   * @param changedFieldCode which code is being changed
   * @param changedFieldValue the value of the code
   * @returns hash containing { <field_name> : true|false }
   */
  function evaluateFormIfCode(
    ifKey: string,
    valueKey: string,
    previousEvaluation: Array<any>,
    fieldsList: Array<any>,
    changedFieldCode: string,
    changedFieldValue: any,
    changedFieldType: string
  ) {
    const evaluation: any = {};

    fieldsList.forEach(field => {
      const fieldIfCode = field[ifKey];
      const fieldIfValue = field[valueKey];

      if (fieldIfCode == null || fieldIfValue == null || fieldIfCode === '') {
        // The checked field has no ifCode requirement. We return last evaluation by default (or true)
        evaluation[field.code] = previousEvaluation[field.code] !== undefined ? previousEvaluation[field.code] : true;
      } else if (fieldIfCode !== changedFieldCode) {
        // The checked field's ifCode is not the same as the changed code. Therefore, this field is not dependant on the change
        // We return last evaluation by default (or true)
        evaluation[field.code] = previousEvaluation[field.code] !== undefined ? previousEvaluation[field.code] : true;
      } else {
        // We try to compare the value and the changed value
        if (changedFieldType === 'checkbox') {
          // fieldIfValue contains string "1" as opposed to true/false since it comes from the server
          evaluation[field.code] = (fieldIfValue === '1') === changedFieldValue;
        } else if (changedFieldType == 'valuelist' || changedFieldType == 'select') {
          // Possible array (if the dependency allows multivalues, we check if the changedvalue has the ifvalue)
          const arrayValues = Array.isArray(changedFieldValue) ? changedFieldValue : [changedFieldValue];
          evaluation[field.code] = arrayValues.findIndex(x => x == fieldIfValue) >= 0;
        } else {
          // Others? just normal comparison
          evaluation[field.code] = fieldIfValue === changedFieldValue;
        }
      }
    });

    return evaluation;
  }

  /**
   *    T22311: get fields (its code) that should be emptied before evaluation by evaluateFormIfCode(). The problem is regarding transitive dependency
   *    e.g. Attribute C is shown if Attribute B has value B1. Attribute B is shown only if Attribute A has value A1
   *        A (A1) -> B (B1) -> C
   *        if A's value is changed to A2, Attribute C will still be shown since the value of attribute B is still B1
   *        so, whenever the value of attribute A is changed, we need to make sure that attribute B's value is cleared so that attribute C won't be shown before we evaluate everything
   *
   *    This method runs recursively. For every field that should be emptied, we use its code as parameter to recursively call the same method
   *    so we can make sure that chaining dependency gets resolved no matter how far the chain is
   *    e.g. A (A1) -> B (B1) -> C (C1) -> D (D1) -> E
   *        based on the structure above,
   *        if we change the value of attribute A, we need to clear value of Attribute B, C, D, and E
   *        if we change the value of attribute B, we need to clear value of Attribute C, D, and E
   *        if we change the value of attribute C, we need to clear value of Attribute D and E
   *
   *    IMPORTANT: This can result in unending recursion if you make 2 fields dependant on each other! So don't do it.
   *
   * @param ifKey the key for visibility dependant field
   * @param ifValue the key for visibility dependant value
   * @param fieldList List of fields
   * @param changedFieldCode the code of the field whose value is changed
   * @param changedFieldValue the changed value
   * @param fieldsToBeEmptied Set of string containing the code of the fields that needs to be emptied. Default is empty Set
   */
  function getTransitiveDependencyFieldsToBeEmptied(
    ifKey: string,
    ifValue: string,
    fieldList: Array<any>,
    changedFieldCode: string,
    changedFieldValue: any,
    fieldsToBeEmptied: Set<string> = new Set<string>()
  ) {
    const changedField = fieldList.find(x => x.code == changedFieldCode);
    if (changedField != null) {
      // Find other fields whose visibility/required depend on this field
      // We have to check the value as well if it matches. If it doesn't match, then we need to remove it
      const affectedFields = fieldList.filter(x => {
        const dependantField = x[ifKey] == changedFieldCode;
        const notItsOwn = x.code != changedFieldCode;
        const valueMatch =
          (Array.isArray(changedFieldValue) && changedFieldValue[0] == x[ifValue]) || changedFieldValue == x[ifValue];
        return dependantField && notItsOwn && !valueMatch;
      });
      affectedFields.forEach(x => {
        fieldsToBeEmptied.add(x.code);
        getTransitiveDependencyFieldsToBeEmptied(ifKey, ifValue, fieldList, x.code, x[ifValue], fieldsToBeEmptied);
      });
    }
    return fieldsToBeEmptied;
  }

  /**
   * T22311: Get fields that can be safely removed depending on visible_if_code and visible_if_value rule
   * It basically goes through every field, find the dependant field, and compare whether the entered value matches the assigned value
   * if not, we can safely remove it
   *
   * Only for attribute fields
   *
   * @param fieldList List of attribute fields
   * @param fieldValues hash of id-value (e.g. { 'attribute-1': "xxx", 'attribute-2': "yyy", .... })
   * @param ifKey the key for visibility dependency. Default is 'visible_if_code'
   * @param valueKey the key for visibility dependency value. Default is 'visible_if_value'
   * @param fieldsToBeEmptied Set of string containing the attribute name of the fields that can be safely removed. Default is empty Set
   * @returns modified `fieldsToBeEmptied`
   */
  function getRemovableAttributeFields(
    fieldList: Array<any>,
    fieldValues: any,
    ifKey = 'visible_if_code',
    valueKey = 'visible_if_value',
    fieldsToBeEmptied: Set<string> = new Set<string>()
  ) {
    fieldList.forEach(x => {
      if (x[ifKey] == null || x[valueKey] == null || x[ifKey] == '') {
        // this field has no dependency. Skip it (don't do anything)
      } else {
        const supposedValue = x[valueKey]; // always scalar
        const dependantField = fieldList.find(y => x[ifKey] == y.code && y.code != x.code);
        if (dependantField != null) {
          const enteredValue = fieldValues['attribute-' + dependantField.id]; // Can be array or scalar
          let valuesAreDifferent = false;
          if (Array.isArray(enteredValue)) {
            valuesAreDifferent = enteredValue.find(x => x == supposedValue) == null;
          } else {
            valuesAreDifferent = supposedValue != enteredValue;
          }

          if (valuesAreDifferent) {
            // if the value is different from the assigned dependency value, we can safely remove this attribute's value from submission
            fieldsToBeEmptied.add('attribute-' + x.id);
            // we need to remove the entered values as well so the next attributes to check have the up-to-date values
            delete fieldValues['attribute-' + x.id];
          }
        }
      }
    });
    return fieldsToBeEmptied;
  }

  return {
    evaluateIfCodeValue,
    evaluateFormIfCode,
    getTransitiveDependencyFieldsToBeEmptied,
    getRemovableAttributeFields,
  };
}
